import algoliasearch, { SearchClient, SearchIndex } from 'algoliasearch';
import type {
  RecommendClient,
  RecommendModel,
  RecommendationsQuery,
  TrendingModel,
  TrendingQuery,
} from '@algolia/recommend';
import recommend from '@algolia/recommend';
import searchInsights from 'search-insights';
import { v4 as uuidv4 } from 'uuid';

class Algolia {
  static readonly COLLECTIONS = 'collections';

  static readonly PAGES = 'pages';

  static readonly PRODUCTS = 'products';

  static readonly VARIANTS = 'variants';

  private client: SearchClient;

  public userID: string;

  public prefix: string;

  public readonly recommendClient: RecommendClient;

  constructor() {
    const { appId, apiKey, prefix } = window.theme.integrations.algolia;
    this.client = algoliasearch(appId, apiKey);

    this.recommendClient = recommend(appId, apiKey);

    searchInsights('init', { appId, apiKey });

    this.prefix = prefix;
    this.setUserID();
  }

  private setUserID() {
    const user = localStorage.getItem('userid');

    if (!user) localStorage.setItem('userid', uuidv4());
    this.userID = localStorage.getItem('userid');
  }

  getIndex(index: string) {
    return this.prefix ? `${this.prefix}-${index}` : index;
  }

  track(event, data) {
    data.index = this.getIndex(data.index);
    data.userToken = this.getUserID();

    searchInsights(event, data);
  }

  getUserID() {
    return this.userID;
  }

  private static getIndexName(name: string): string {
    return [theme.integrations.algolia.prefix, name].filter((value) => value).join('-');
  }

  public index(name: string): SearchIndex {
    return this.client.initIndex(Algolia.getIndexName(name));
  }

  public async recommendations(
    query: RecommendationsQuery | TrendingQuery,
    endpoint: RecommendModel | TrendingModel | string = 'related-products'
  ) {
    // @ts-ignore
    if (query.objectIDs) {
      return this.multiRecommendations(query);
    }

    if (endpoint === 'trending-items') {
      return this.getTrending(query as TrendingQuery);
    }

    return this.getRecommendations(query as RecommendationsQuery);
  }

  private async getTrending(query: Omit<TrendingQuery, 'indexName'>) {
    const recommendations = await this.recommendClient.getTrendingItems([
      {
        indexName: Algolia.getIndexName(Algolia.PRODUCTS),
        ...query,
      },
    ]);

    return recommendations;
  }

  private async getRecommendations(query: Omit<RecommendationsQuery, 'indexName'>) {
    const recommendations = await this.recommendClient.getRecommendations([
      {
        indexName: Algolia.getIndexName(Algolia.PRODUCTS),
        ...query,
      },
    ]);

    return recommendations;
  }

  private async multiRecommendations(query) {
    const queries = [];
    query.objectIDs.forEach((id) => {
      delete query.objectIDs;
      query.objectID = id;
      queries.push({
        indexName: Algolia.getIndexName(Algolia.PRODUCTS),
        ...query,
      });
    });

    const recommendations = await this.recommendClient.getRecommendations(queries);

    return recommendations;
  }
}

export default Algolia;
